/**
 * Brand × city hubs `/{cc}/{lang}/brand/:brand/:city` (web plan W3.3,
 * master D-5): the rules the SPA page (`src/pages/BrandCityPage.tsx`) and the
 * SEO server (`server/routes.mjs`) share, so both query the backend's
 * `brandCityPage` the same way.
 *
 * - A pair is indexable when the backend's `seo.indexable` is true (≥ 3
 *   publicly visible stores in the city — D-5).
 * - An unknown brand/city slug pair → 404 (noindex).
 * - Canonical mismatch → 301 to `urlPath`.
 * - H1: "{Brand} Stores in {City}" (always).
 * - `?page=N` pages the live promotions, LIST_PAGE_SIZE a page.
 *
 * Pure (no React, no `window`).
 */
import type { Brand, EntitySeo, Promotion } from '@/types';
import { localeKey, type Locale } from './locales';
import { brandSeoName, cleanName } from './text';
import { LIST_PAGE_SIZE, lastListPage } from './urls';

/** A city reference as the backend returns it. */
export interface CityRef {
  id: string;
  name: string;
  slug: string;
  urlPath?: string | null;
}

/** A store on the brand × city hub. */
export interface BrandCityStore {
  id: string;
  name: string;
  /** "{Brand} – {Area}, {City}" or a shorter form (backend B3.2). */
  displayName?: string | null;
  address?: string | null;
  /** Neighbourhood, market or mall (B3.2). */
  area?: string | null;
  phoneNumber?: string | null;
  openingHours?: readonly { days: readonly string[]; opens: string; closes: string }[] | null;
  /** "lat,lng" string — present for geoVerified stores. */
  location?: string | null;
  urlPath?: string | null;
}

/** The backend's `brandCityPage` answer, as the SPA and the server select it. */
export interface BrandCityHubPage {
  /** Canonical path: serve only there, 301 to it from any other spelling. */
  urlPath: string;
  brand: Brand & { seo?: EntitySeo | null };
  city: CityRef;
  /** By name; storeCount ≥ 3 to be indexable. */
  stores: readonly BrandCityStore[];
  /** Their number: ≥ 3 to be indexable (D-5). */
  storeCount: number;
  /** Offers valid at one or more of those stores. */
  livePromotions: readonly Promotion[];
  upcomingPromotions: readonly Promotion[];
  /** The backend's index decision for this hub (D-5). */
  seo: EntitySeo;
}

/** One route segment: decoded, trimmed, lower case; null when not usable. */
function hubSegment(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  value = value.trim().toLowerCase();
  // A slug is one path segment: no separators, no whitespace or control chars.
  if (!value || /[/\\?#\s\p{Cc}]/u.test(value)) return null;
  return value;
}

/**
 * The query key for the brand × city hub from the route's `:brand` and `:city`
 * segments. Null for segments that can never be valid slugs.
 */
export function brandCityHubKey(
  brand: string | null | undefined,
  city: string | null | undefined,
): { brand: string; city: string } | null {
  const b = hubSegment(brand);
  const c = hubSegment(city);
  if (!b || !c) return null;
  return { brand: b, city: c };
}

/** Variables the SPA hook and the SEO server pass to `brandCityPage`. */
export function brandCityPageVariables(
  key: { brand: string; city: string },
  locale: Locale,
): { brand: string; city: string; locale: string } {
  return { brand: key.brand, city: key.city, locale: localeKey(locale) };
}

/** The brand's display name: cleaned. */
export function brandCityBrandName(brand: Pick<Brand, 'name' | 'seoName'>): string {
  return brandSeoName(brand) || brand.name;
}

/** The city's display name: cleaned. */
export function brandCityCityName(city: Pick<CityRef, 'name'>): string {
  return cleanName(city.name) || city.name;
}

// Re-export the D-5 link rule and the site-path helper (same as cityHubs).
export { linkedFromHubs, sitePath } from './hubLinks';

/** The hub's last `?page=` for live promotions (an empty list still has page 1). */
export function brandCityHubLastPage(hub: Pick<BrandCityHubPage, 'livePromotions'>): number {
  return lastListPage(hub.livePromotions.length);
}

/** The live promotions for page `page`. */
export function brandCityPromotionsOn(
  hub: Pick<BrandCityHubPage, 'livePromotions'>,
  page: number,
): readonly Promotion[] {
  const n = Number.isSafeInteger(page) && page >= 1 ? page : 1;
  return hub.livePromotions.slice((n - 1) * LIST_PAGE_SIZE, n * LIST_PAGE_SIZE);
}
